import * as Sentry from '@sentry/react'

import { pageLifecycleIntegration } from './integrations/pageLifecycleIntegration'
import type { LogIntegration } from './models'

type LoggingOptions = {
  dsn: string
  release: string | undefined
  environment: string | undefined
  initialScope?: Sentry.BrowserOptions['initialScope']
  integrations?: LogIntegration[]
}

const disabledEnvironments = new Set(['local', 'development'])

const initLogging = ({
  dsn,
  release,
  environment,
  initialScope,
  integrations = [],
}: LoggingOptions) => {
  if (!release || release === 'local') return undefined
  if (!environment || disabledEnvironments.has(environment)) return undefined

  return Sentry.init({
    dsn,
    release,
    environment,
    initialScope,
    normalizeDepth: 10,
    integrations: [
      Sentry.captureConsoleIntegration(),
      Sentry.extraErrorDataIntegration({ depth: 10 }),
      Sentry.featureFlagsIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.reportingObserverIntegration(),
      pageLifecycleIntegration(),
      ...integrations,
    ],
  })
}

const addLogFeatureFlags = (flags: Record<string, boolean>) => {
  const flagsIntegration =
    Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
      'FeatureFlags'
    )
  if (!flagsIntegration) return

  Object.entries(flags).forEach(([name, value]) => {
    flagsIntegration.addFeatureFlag(name, value)
  })
}

const logMessage = Sentry.captureMessage
const logError = Sentry.captureException
const logEvent = Sentry.captureEvent
const logBreadcrumb = Sentry.addBreadcrumb
const setLogUser = Sentry.setUser
const setLogTag = Sentry.setTag
const setLogTags = Sentry.setTags
const addLogIntegration = Sentry.addIntegration

export {
  initLogging,
  addLogFeatureFlags,
  logMessage,
  logError,
  logEvent,
  logBreadcrumb,
  setLogUser,
  setLogTag,
  setLogTags,
  addLogIntegration,
}
